var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Dropdown, Input, Table as AntdTable, Tooltip, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { assertCheckboxInputCell, CheckboxInputCell } from "./components/CheckboxInputCell";
import { assertSelectInputCell, SelectInputCell } from "./components/SelectInputCell";
import "./EditableTable.scss";
import { ResizableTitle } from "./ResizableTitle";
import { compact } from "lodash";
import { assertTreeSelectInputCell, TreeSelectInputCell } from "./components/TreeSelectInputCell";
/**
 * A resizable table with (optional) editable cells.
 * Refer to [Ant Design's Table](https://ant.design/components/table/)
 * component for all available props.
 */
export function EditableTable(_a) {
    var _b;
    var { columns = [], getMenuProps, onCheckboxChange, onInputChange, onSelectChange, rowSelection, onColumnSizeChange, size } = _a, props = __rest(_a, ["columns", "getMenuProps", "onCheckboxChange", "onInputChange", "onSelectChange", "rowSelection", "onColumnSizeChange", "size"]);
    const [columnsWidth, setColumnsWidth] = useState(compact([
        rowSelection ? { width: rowSelection.columnWidth } : null,
        ...columns
    ]).map(({ width }) => (width !== undefined ? Number(width) : width)));
    const tmpColumnsWidth = useRef(columnsWidth);
    const tableRef = useRef(null);
    useEffect(() => {
        var _a;
        if (!((_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.nativeElement))
            return;
        const headCells = tableRef.current.nativeElement.querySelectorAll("th");
        const tmpWidth = [];
        headCells.forEach((cell, cellIdx) => (tmpWidth[cellIdx] = cell.offsetWidth));
        setColumnsWidth(tmpWidth);
        tmpColumnsWidth.current = tmpWidth;
    }, [columns === null || columns === void 0 ? void 0 : columns.length]);
    const updatedColumns = useMemo(() => {
        return columns.map((column, idx) => {
            const columnIdx = rowSelection ? idx + 1 : idx;
            // Built in columns like the expand & selection do not have a key, and should not be extended
            if (!("key" in column)) {
                return column;
            }
            return Object.assign(Object.assign({}, column), { width: !column.resizable || isNaN(Number(columnsWidth[columnIdx]))
                    ? column.width
                    : Number(columnsWidth[columnIdx]), 
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                render: (value, record, index) => {
                    let cell = null;
                    if (column.render) {
                        cell = column.render(value, record, index);
                    }
                    else if (assertSelectInputCell(column.input)) {
                        cell = (_jsx(SelectInputCell, { style: column.input.style, columnKey: column.key, defaultValue: value, onClick: preventPropagation, onSelectChange: onSelectChange, options: column.input.options, record: record, popupRenderIncludingChangeCallback: column.input.popupRenderIncludingChangeCallback }));
                    }
                    else if (assertTreeSelectInputCell(column.input)) {
                        cell = (_jsx(TreeSelectInputCell, { style: column.input.style, columnKey: column.key, defaultValue: value, onClick: preventPropagation, onSelectChange: onSelectChange, treeData: column.input.options, record: record }));
                    }
                    else if (assertCheckboxInputCell(column.input)) {
                        cell = (_jsx(CheckboxInputCell, { style: column.input.style, columnKey: column.key, defaultChecked: value, onChange: onCheckboxChange, onClick: preventPropagation }));
                    }
                    else if (column.input) {
                        cell = (_jsx(Input, Object.assign({}, column.input, { style: column.input.style, defaultValue: value, onChange: onInputChange, onClick: preventPropagation })));
                    }
                    else {
                        cell = (_jsx(Tooltip, { title: value, mouseEnterDelay: 0.4, children: _jsx(Typography.Text, { ellipsis: true, children: value }) }));
                    }
                    if (getMenuProps) {
                        return (_jsx(Dropdown, { menu: getMenuProps(record), trigger: ["contextMenu"], children: _jsx("div", { style: { width: "100%", height: "100%" }, children: cell }) }));
                    }
                    return cell;
                }, 
                // Not use `key` here, omit to avoid type issue
                onHeaderCell: (col) => ({
                    width: col.width,
                    onResizeCallback: !col.resizable || columnsWidth[columnIdx] === undefined
                        ? undefined
                        : (e, { size, node }) => {
                            var _a, _b, _c;
                            if (!node)
                                return;
                            const cellIndex = 1 + ((_a = node.closest("th")) === null || _a === void 0 ? void 0 : _a.cellIndex);
                            if (cellIndex === undefined)
                                return;
                            const cols = (_b = node
                                .closest(".ant-table-container")) === null || _b === void 0 ? void 0 : _b.querySelectorAll(`colgroup col:nth-of-type(${cellIndex})`);
                            if (cols) {
                                for (let i = 0; i < cols.length; i += 1) {
                                    const col = cols[i];
                                    const currentWidth = Number(col.style.width.replace("px", ""));
                                    const updatedSize = currentWidth +
                                        size.width -
                                        Number(columnsWidth[columnIdx]);
                                    col["style"].width = `${updatedSize}px`;
                                    tmpColumnsWidth.current = [
                                        ...tmpColumnsWidth.current.slice(0, columnIdx),
                                        updatedSize,
                                        ...tmpColumnsWidth.current.slice(columnIdx + 1)
                                    ];
                                }
                            }
                            const virtualTableCells = (_c = node
                                .closest(".ant-table-container")) === null || _c === void 0 ? void 0 : _c.querySelectorAll(`.ant-table-tbody-virtual-holder .ant-table-row .ant-table-cell:nth-of-type(${cellIndex})`);
                            if (virtualTableCells) {
                                for (let i = 0; i < virtualTableCells.length; i += 1) {
                                    const cell = virtualTableCells[i];
                                    const currentWidth = cell.clientWidth;
                                    const updatedSize = currentWidth +
                                        size.width -
                                        Number(columnsWidth[columnIdx]);
                                    cell["style"].width = `${updatedSize}px`;
                                    cell["style"].minWidth = `${updatedSize}px`;
                                    cell["style"].flex = `0 0 ${updatedSize}px`;
                                    const row = cell.closest(".ant-table-row");
                                    row.style.width = `${row.clientWidth + updatedSize - currentWidth}px`;
                                }
                            }
                        },
                    onResizeStop: () => {
                        setColumnsWidth([...tmpColumnsWidth.current]);
                        if (typeof onColumnSizeChange === "function" &&
                            tmpColumnsWidth.current[columnIdx]) {
                            onColumnSizeChange({
                                [column.key]: tmpColumnsWidth.current[columnIdx]
                            });
                        }
                    }
                }) });
        });
    }, [columns, getMenuProps, columnsWidth, rowSelection, onSelectChange]);
    const expandable = useMemo(() => {
        var _a;
        return ((_a = props.expandable) !== null && _a !== void 0 ? _a : {
            showExpandColumn: false
        });
    }, [props.expandable]);
    return (_jsx(AntdTable, Object.assign({ ref: tableRef, columns: updatedColumns, expandable: expandable, rowSelection: rowSelection, size: size, components: Object.assign({ header: Object.assign({ cell: ResizableTitle }, (_b = props.components) === null || _b === void 0 ? void 0 : _b.header) }, props.components) }, props)));
}
function preventPropagation(event) {
    event.stopPropagation();
}
